import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogActions,
  Snackbar,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { DataGrid } from "@mui/x-data-grid";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import AddProjects from "./AddProjects";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useUser } from "../context/UserContext";
import "./Projects.css";

const Projects = () => {
  const { user } = useUser();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Fetch all projects for user's organization
  const fetchProjects = async () => {
    if (!user?.organizationId) return;
    try {
      const q = query(
        collection(db, "projects"),
        where("organizationId", "==", user.organizationId)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((doc, index) => ({
        id: doc.id,
        index: index + 1,
        ...doc.data(),
      }));
      setProjects(data);
    } catch (err) {
      console.error("Error fetching projects:", err);
    }
  };

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organizationId]);

  // Handle edit click
  const handleEdit = (project) => {
    setEditProject(project);
    setOpen(true);
  };

  // Handle delete
  const handleDelete = async () => {
    if (projectToDelete) {
      try {
        await deleteDoc(doc(db, "projects", projectToDelete));
        showSnackbar("Project deleted successfully!", "success");
        fetchProjects();
        setDeleteDialogOpen(false);
      } catch (error) {
        console.error(error);
        showSnackbar("Failed to delete project", "error");
      }
    }
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 1 },
    {
      field: "sdgs",
      headerName: "SDGs",
      flex: 1,
      renderCell: (params) => params.value?.join(", "),
    },
    {
      field: "sectors",
      headerName: "Sectors",
      flex: 1,
      renderCell: (params) => params.value?.join(", "),
    },
    { field: "budget", headerName: "Budget", flex: 0.8 },
    {
      field: "states",
      headerName: "States",
      flex: 1.2,
      renderCell: (params) => params.value?.join(", "),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Tooltip title="Edit">
            <IconButton onClick={() => handleEdit(params.row)} size="small">
              <EditIcon sx={{ color: "#645792" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              onClick={() => {
                setProjectToDelete(params.row.id);
                setDeleteDialogOpen(true);
              }}
              size="small"
            >
              <DeleteIcon sx={{ color: "#ad1010" }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box p={4} sx={{ width: "100%", overflowX: "auto", position: "relative" }}>
      <Box sx={{ width: "100%", maxWidth: "1200px", mx: "auto" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: "Lato, sans-serif",
              color: "#645792",
              fontWeight: "bold",
            }}
          >
            Projects
          </Typography>
          <Button
            className="project-button"
            sx={{ textTransform: "none" }}
            onClick={() => {
              setEditProject(null);
              setOpen(true);
            }}
          >
            + Add Project
          </Button>
        </Box>

        <Box
          sx={{
            width: "100%",
            transition: "all 0.3s ease",
            overflow: "hidden",
            flexGrow: 1,
          }}
        >
          <DataGrid
            className="grid"
            rows={projects}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            sx={{
              mt: 2,
              borderRadius: 2,
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "rgba(180, 87, 146,0.1)",
              },
            }}
          />
        </Box>

        {/* Add/Edit Project Dialog */}
        <AddProjects
          open={open}
          handleClose={() => setOpen(false)}
          onProjectAdded={fetchProjects}
          editProject={editProject}
        />

        {/* Delete Confirmation */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>
            Are you sure you want to delete this project?
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              color="error"
              sx={{ textTransform: "none" }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default Projects;
